import posthog from 'posthog-js'
import type { TrackingAdapter, TrackingOptions, TrackingUser } from '@saas/lib/tracking-core'

export interface PosthogWebConfig {
  apiKey: string;
  apiHost?: string;
}

/**
 * PostHog adapter for the web (React / SPA).
 * The only module in the monorepo that depends on `posthog-js`.
 */
export function createPosthogWebAdapter(
    config: PosthogWebConfig,
): TrackingAdapter {
    return {
        init(): void {
            if (!config.apiKey) {
                console.warn('[tracking] Missing PostHog API key, tracking disabled')
                return
            }
            posthog.init(config.apiKey, {
                api_host: config.apiHost ?? 'https://eu.i.posthog.com',
                capture_pageview: false, // managed manually via trackPageView
                person_profiles: 'identified_only',
            })
        },

        identify(user: TrackingUser): void {
            const { id, ...traits } = user
            posthog.identify(id, traits)
        },

        reset(): void {
            posthog.reset()
        },

        track(
            eventName: string,
            properties: Record<string, unknown> = {},
            options?: TrackingOptions,
        ): void {
            posthog.capture(eventName, properties, options?.sendInstantly ? { send_instantly: true } : undefined)
        },

        trackPageView(
            pageName: string,
            properties: Record<string, unknown> = {},
        ): void {
            posthog.capture('$pageview', { page: pageName, ...properties })
        },
    }
}
